import { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Switch } from '../../../components/ui/switch';
import { Plus, Edit, Trash2, BookOpen, Eye, EyeOff, Calendar, Image, Link, Heart, Users } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../../services/api';
import RichTextEditor from '../../../components/RichTextEditor';
import ImageUpload from '../../../components/ImageUpload';
import ImagePickerDialog from './ImagePickerDialog';

const ArticlesManager = () => {
  const [articles, setArticles] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    featured_image: '',
    category: 'faith',
    external_link: '',
    is_visible: true,
    published_at: new Date().toISOString().split('T')[0]
  });

  const categoryOptions = [
    { value: 'faith', label: 'Trosartiklar', icon: Heart, color: 'text-purple-600' },
    { value: 'social', label: 'Sociala artiklar', icon: Users, color: 'text-blue-600' },
    { value: 'education', label: 'Vuxenundervisning', icon: BookOpen, color: 'text-green-600' }
  ];

  const loadArticles = async () => {
    try {
      const data = await api.getArticles(null);
      setArticles(data);
    } catch (error) {
      console.error('Error loading articles:', error);
      toast.error('Kunde inte ladda artiklar');
    }
  };

  useEffect(() => {
    loadArticles();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        published_at: new Date(formData.published_at).toISOString()
      };

      if (editingArticle) {
        await api.updateArticle(editingArticle.id, submitData);
        toast.success('Artikel uppdaterad!');
      } else {
        await api.createArticle(submitData);
        toast.success('Artikel skapad!');
      }
      setIsDialogOpen(false);
      resetForm();
      loadArticles();
    } catch (error) {
      console.error('Error saving article:', error);
      toast.error('Ett fel uppstod');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Är du säker på att du vill radera denna artikel?')) {
      try {
        await api.deleteArticle(id);
        toast.success('Artikel raderad!');
        loadArticles();
      } catch (error) {
        toast.error('Kunde inte radera artikeln');
      }
    }
  };

  const handleToggleVisibility = async (article) => {
    try {
      await api.updateArticle(article.id, {
        ...article,
        is_visible: !article.is_visible
      });
      toast.success(article.is_visible ? 'Artikel dold' : 'Artikel synlig');
      loadArticles();
    } catch (error) {
      toast.error('Kunde inte uppdatera synlighet');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      featured_image: '',
      category: 'faith',
      external_link: '',
      is_visible: true,
      published_at: new Date().toISOString().split('T')[0]
    });
    setEditingArticle(null);
  };

  const openEditDialog = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title || '',
      content: article.content || '',
      excerpt: article.excerpt || '',
      featured_image: article.featured_image || '',
      category: article.category || 'faith',
      external_link: article.external_link || '',
      is_visible: article.is_visible !== false,
      published_at: article.published_at ? article.published_at.split('T')[0] : new Date().toISOString().split('T')[0]
    });
    setIsDialogOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('sv-SE');
  };

  const getCategoryInfo = (category) => {
    return categoryOptions.find(c => c.value === category) || categoryOptions[0];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookOpen className="w-8 h-8 text-purple-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Fördjupning - Artiklar</h2>
            <p className="text-gray-600">Hantera artiklar om tro och samhälle</p>
          </div>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setIsDialogOpen(true);
          }}
          className="bg-purple-500 hover:bg-purple-600 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ny artikel
        </Button>
      </div>

      {/* Articles List */}
      <div className="grid gap-4">
        {articles.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-8 text-center text-gray-500">
              <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Inga artiklar ännu. Skapa din första artikel!</p>
            </CardContent>
          </Card>
        ) : (
          articles.map((article) => {
            const catInfo = getCategoryInfo(article.category);
            const CatIcon = catInfo.icon;
            return (
              <Card key={article.id} className={`${!article.is_visible ? 'opacity-60 bg-gray-50' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Featured Image */}
                    {article.featured_image && (
                      <img
                        src={article.featured_image}
                        alt=""
                        className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                      />
                    )}
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 truncate">{article.title}</h3>
                        {!article.is_visible && (
                          <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">Dold</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                        {article.excerpt || article.content?.substring(0, 150)}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(article.published_at)}
                        </span>
                        <span className={`flex items-center gap-1 ${catInfo.color}`}>
                          <CatIcon className="w-3 h-3" />
                          {catInfo.label}
                        </span>
                        {article.external_link && (
                          <span className="flex items-center gap-1 text-blue-600">
                            <Link className="w-3 h-3" />
                            Extern länk
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleVisibility(article)}
                        title={article.is_visible ? 'Dölj' : 'Visa'}
                      >
                        {article.is_visible ? (
                          <Eye className="w-4 h-4 text-green-600" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(article)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(article.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingArticle ? 'Redigera artikel' : 'Skapa ny artikel'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titel (Svenska) *
              </label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Artikelns titel..."
                required
              />
              <p className="text-xs text-gray-500 mt-1">Översätts automatiskt till andra språk</p>
            </div>

            {/* Excerpt */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sammanfattning (Svenska)
              </label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder="Kort sammanfattning av artikeln..."
                rows={2}
              />
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Innehåll (Svenska) *
              </label>
              <RichTextEditor
                value={formData.content}
                onChange={(value) => setFormData({ ...formData, content: value })}
                placeholder="Skriv artikelns innehåll här..."
                minHeight="200px"
              />
              <p className="text-xs text-gray-500 mt-1">Använd verktygsfältet för att formatera text. Översätts automatiskt.</p>
            </div>

            {/* Featured Image */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Image className="w-4 h-4 inline mr-1" />
                Utvald bild
              </label>
              <ImageUpload
                value={formData.featured_image}
                onChange={(url) => setFormData({ ...formData, featured_image: url })}
                aspectRatio="video"
                placeholder="Klicka eller dra för att ladda upp bild"
              />
              <div className="flex items-center gap-2 mt-2">
                <ImagePickerDialog 
                  currentImage={formData.featured_image}
                  onSelect={(url) => setFormData({ ...formData, featured_image: url })}
                />
                <span className="text-xs text-gray-500">eller ange URL:</span>
              </div>
              <Input
                value={formData.featured_image}
                onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                placeholder="https://example.com/image.jpg"
                className="mt-1"
              />
            </div>

            {/* External Link */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Link className="w-4 h-4 inline mr-1" />
                Extern länk (valfritt)
              </label>
              <Input
                value={formData.external_link}
                onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                placeholder="https://example.com/original-article"
              />
              <p className="text-xs text-gray-500 mt-1">Länk till ursprunglig artikel om den finns externt</p>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kategori *
              </label>
              <div className="flex gap-4">
                {categoryOptions.map((cat) => {
                  const CatIcon = cat.icon;
                  return (
                    <label key={cat.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="category"
                        value={cat.value}
                        checked={formData.category === cat.value}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                      />
                      <CatIcon className={`w-4 h-4 ${cat.color}`} />
                      <span className="text-sm">{cat.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Publiceringsdatum
              </label>
              <Input
                type="date"
                value={formData.published_at}
                onChange={(e) => setFormData({ ...formData, published_at: e.target.value })}
              />
            </div>

            {/* Visibility */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <label className="text-sm font-medium text-gray-700">Synligt</label>
                <p className="text-xs text-gray-500">Visa artikeln på hemsidan</p>
              </div>
              <Switch
                checked={formData.is_visible}
                onCheckedChange={(checked) => setFormData({ ...formData, is_visible: checked })}
              />
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Avbryt
              </Button>
              <Button
                type="submit"
                className="bg-purple-500 hover:bg-purple-600 text-white"
              >
                {editingArticle ? 'Uppdatera' : 'Skapa'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArticlesManager;
